import * as Sentry from '@sentry/react'
import { FRAME_TITLES } from './constants'

// Canvas export dimensions
const PANEL_WIDTH = 720
const PANEL_HEIGHT = 480
const GRID_PADDING = 8
const TITLE_HEIGHT = 32
const LINE_HEIGHT = 28
const CAPTION_PADDING = 16
const MAX_CAPTION_WIDTH = PANEL_WIDTH - 24
const BADGE_SIZE = 72
const BADGE_OFFSET = 16
const LOGO_BORDER_RADIUS = 8

/** Calculates the number of wrapped lines needed for text at a given max width */
const calculateLinesNeeded = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): number => {
  if (!text) return 1
  const words = text.split(' ')
  let line = ''
  let lineCount = 1

  for (const word of words) {
    const testLine = line + word + ' '
    const metrics = ctx.measureText(testLine)
    if (metrics.width > maxWidth && line !== '') {
      lineCount++
      line = word + ' '
    } else {
      line = testLine
    }
  }
  return lineCount
}

/** Loads an image from URL with CORS support */
const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })

/**
 * Creates a combined canvas from 4 storyboard panel images with captions.
 * Pure function — no React hooks, no side effects.
 */
export const createStoryboardCanvas = async (
  storyboardUrls: string[],
  frameCaptions: string[] | undefined
): Promise<HTMLCanvasElement | null> => {
  if (storyboardUrls.length !== 4) return null

  try {
    const images = await Promise.all(storyboardUrls.map(loadImage))
    const captions = frameCaptions ?? []

    // Measure text to calculate dynamic caption heights
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) return null
    tempCtx.font = '24px system-ui, -apple-system, sans-serif'

    const captionLinesPerPanel = [0, 1, 2, 3].map((i) =>
      calculateLinesNeeded(tempCtx, captions[i] || '', MAX_CAPTION_WIDTH)
    )

    const row1MaxLines = Math.max(captionLinesPerPanel[0], captionLinesPerPanel[1], 2)
    const row2MaxLines = Math.max(captionLinesPerPanel[2], captionLinesPerPanel[3], 2)

    const row1CaptionHeight = TITLE_HEIGHT + row1MaxLines * LINE_HEIGHT + CAPTION_PADDING
    const row2CaptionHeight = TITLE_HEIGHT + row2MaxLines * LINE_HEIGHT + CAPTION_PADDING

    const row1TotalHeight = PANEL_HEIGHT + row1CaptionHeight
    const row2TotalHeight = PANEL_HEIGHT + row2CaptionHeight

    const canvasWidth = PANEL_WIDTH * 2 + GRID_PADDING * 3
    const canvasHeight = row1TotalHeight + row2TotalHeight + GRID_PADDING * 3

    const canvas = document.createElement('canvas')
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, canvasWidth, canvasHeight)

    const positions = [
      { x: GRID_PADDING, y: GRID_PADDING, captionHeight: row1CaptionHeight },
      { x: GRID_PADDING * 2 + PANEL_WIDTH, y: GRID_PADDING, captionHeight: row1CaptionHeight },
      { x: GRID_PADDING, y: GRID_PADDING * 2 + row1TotalHeight, captionHeight: row2CaptionHeight },
      {
        x: GRID_PADDING * 2 + PANEL_WIDTH,
        y: GRID_PADDING * 2 + row1TotalHeight,
        captionHeight: row2CaptionHeight,
      },
    ]

    images.forEach((img, index) => {
      const pos = positions[index]

      // Draw image
      ctx.drawImage(img, pos.x, pos.y, PANEL_WIDTH, PANEL_HEIGHT)

      // Draw panel number badge
      const badgeX = pos.x + BADGE_OFFSET
      const badgeY = pos.y + BADGE_OFFSET
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
      ctx.beginPath()
      ctx.arc(badgeX + BADGE_SIZE / 2, badgeY + BADGE_SIZE / 2, BADGE_SIZE / 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#FFFFFF'
      ctx.font = 'bold 40px Arial, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(index + 1), badgeX + BADGE_SIZE / 2, badgeY + BADGE_SIZE / 2)
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'

      // Draw caption box below image
      const captionY = pos.y + PANEL_HEIGHT
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(pos.x, captionY, PANEL_WIDTH, pos.captionHeight)

      ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)'
      ctx.lineWidth = 1
      ctx.strokeRect(pos.x, captionY, PANEL_WIDTH, pos.captionHeight)

      // Draw frame title
      ctx.fillStyle = '#888888'
      ctx.font = '600 20px system-ui, -apple-system, sans-serif'
      ctx.fillText(FRAME_TITLES[index], pos.x + 12, captionY + 24)

      // Draw caption text (word-wrapped)
      const captionText = captions[index] || ''
      ctx.fillStyle = '#1a1a1a'
      ctx.font = '24px system-ui, -apple-system, sans-serif'

      const words = captionText.split(' ')
      let line = ''
      let lineY = captionY + TITLE_HEIGHT + LINE_HEIGHT

      for (const word of words) {
        const testLine = line + word + ' '
        const metrics = ctx.measureText(testLine)
        if (metrics.width > MAX_CAPTION_WIDTH && line !== '') {
          ctx.fillText(line.trim(), pos.x + 12, lineY)
          line = word + ' '
          lineY += LINE_HEIGHT
        } else {
          line = testLine
        }
      }
      if (line.trim()) {
        ctx.fillText(line.trim(), pos.x + 12, lineY)
      }
    })

    // Draw bandos.ai logo in top right
    ctx.font = 'bold 28px Poppins, sans-serif'
    const logoText = 'bandos.ai'
    const logoMetrics = ctx.measureText(logoText)
    const logoPadding = 12
    const logoHeight = 36
    const logoX = canvasWidth - 20 - logoMetrics.width - logoPadding
    const logoY = 8

    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    ctx.beginPath()
    ctx.roundRect(logoX, logoY, logoMetrics.width + logoPadding * 2, logoHeight, LOGO_BORDER_RADIUS)
    ctx.fill()

    ctx.fillStyle = '#FFFFFF'
    ctx.textAlign = 'right'
    ctx.fillText(logoText, canvasWidth - 20, logoY + 26)

    return canvas
  } catch (error) {
    console.error('Failed to create storyboard canvas:', error)
    Sentry.captureException(error, {
      tags: { feature: 'mindmap.storyboard', action: 'create_storyboard_canvas' },
    })
    return null
  }
}
